import type { SourceConfig } from '@viva/core';
import type { FormEvent, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { configureRelay } from '../catalog';
import { setUhdRelayCut } from '../uhd-access';
import { LanguagePicker } from './LanguagePicker';
import type { RelayConfig } from '../network';
import type { Country } from './countries';
import { COUNTRIES, fullNumber, guessCountry } from './countries';
import { GateBackdrop } from './GateBackdrop';
import { LANGUAGES, market, setLanguage, t, useLanguage } from '../i18n';
import type { Language } from '../i18n';
import { PASSWORD_RULES, isStrongPassword } from './password-rules';
import { PhoneField } from './PhoneField';
import { useCatalog } from '../state/catalog-context';
import type { CatalogChoice } from '../catalog-languages';
import type { Access, AccountInfo, ApiResult, PlaybackSlot, Playlist, SessionData, SignupInput, SportsToday, Watching } from './api';
import * as api from './api';
import type { DeviceIdentity } from './device';
import { deviceIdentity } from './device';

/**
 * Access control: the app opens only for a logged-in account with an active
 * plan. People sign up with their email (verified with a code) and WhatsApp
 * number and request a free trial, which the admin approves (one trial per
 * device, number and email), or log in with details the admin sent them.
 * The account's playlist is loaded automatically; users never enter
 * provider details. The session is re-checked regularly and when the app
 * returns to the foreground, so an ended trial or a disabled account locks
 * the app.
 */

const SESSION_KEY = 'viva.session';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** While a trial request waits for approval, so the app unlocks soon after. */
const PENDING_CHECK_INTERVAL_MS = 10 * 1000;
/** Browser version: the relay's address changes when it restarts (quick tunnel), so it is picked up quickly. */
const RELAY_CHECK_INTERVAL_MS = 60 * 1000;
/** How long the app keeps working when the server can't be reached. */
const OFFLINE_GRACE_MS: Record<AccountInfo['kind'], number> = { none: 0, trial: 10 * 60 * 1000, paid: 3 * 24 * 60 * 60 * 1000 };

interface StoredSession {
  token: string;
  data: SessionData;
  /** serverTime − local clock at the last successful check. */
  clockOffset: number;
  verifiedAt: number;
}

interface AccountState {
  account: AccountInfo;
  access: Access;
  trialHours: number;
  supportWhatsapp: string;
  /** Current time by the server's clock (ms). */
  serverNow: () => number;
  logOut: () => void;
  /** Deletes the account; resolves to an error message, or null when deleted. */
  deleteAccount: (password: string) => Promise<string | null>;
  /** Loads the account's playlist again (after a failed first load). */
  reloadPlaylist: () => void;
  /** Re-checks the session now (fresh relay address and pass) before a manual update. */
  refreshSession: () => Promise<void>;
  /** Today's football matches and their tested channels. */
  sports: () => Promise<ApiResult<SportsToday>>;
  /** Viewers confirm which channel has the match (#156): ask this viewer?, and their answer. */
  matchQuestion: (matchId: string, streamId: number) => Promise<boolean>;
  answerMatchQuestion: (matchId: string, streamId: number, yes: boolean) => Promise<boolean>;
  /** Reports a search the viewer typed (statistics). */
  reportSearch: (search: string) => void;
  /** The languages of the channel list (line pool), and a change of them (the list then reloads). */
  catalog: CatalogChoice | null;
  setCatalog: (choice: CatalogChoice) => Promise<string | null>;
  /** Stream limits (Standard 1, Premium 2): beyond them the viewer picks a screen to stop. */
  playback: {
    /** Trial on the shared pool: streams wait for a free line. */
    pooled: boolean;
    start: (watching?: Watching, replaces?: string, stop?: string) => Promise<ApiResult<PlaybackSlot>>;
    heartbeat: (leaseId: string, watching?: Watching) => Promise<ApiResult<{ ok: boolean; reason?: string; ticket?: string | null }>>;
    stop: (leaseId: string) => void;
  };
}

const AccountContext = createContext<AccountState | null>(null);

export function useAccount(): AccountState {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccount outside AccountGate');
  return value;
}

/** The account, when inside an active account (absent in isolated component tests). */
export function useOptionalAccount(): AccountState | null {
  return useContext(AccountContext);
}

/** "Keep me logged in" (on by default): off keeps the login only until the browser or app is closed. */
const REMEMBER_KEY = 'viva.rememberLogin';

function rememberLogin(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== '0';
  } catch {
    return true;
  }
}

function setRememberLogin(remember: boolean): void {
  try {
    localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
  } catch {
    // Without storage the choice isn't kept; the login then lasts until the app closes.
  }
}

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY) ?? sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeSession(session: StoredSession | null): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    if (session) (rememberLogin() ? localStorage : sessionStorage).setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Without storage the user logs in again on the next launch.
  }
}

function sameConfig(a: SourceConfig, b: SourceConfig): boolean {
  if (a.type === 'm3u' && b.type === 'm3u') return a.url === b.url;
  if (a.type === 'xtream' && b.type === 'xtream') return a.serverUrl === b.serverUrl && a.username === b.username && a.password === b.password;
  return false;
}

/** The relay as reachable from this page: in development a "localhost" relay is on the same host as the page (phones on the LAN). */
function reachableRelay(relay: RelayConfig | null): RelayConfig | null {
  if (!relay || !import.meta.env.DEV || typeof location === 'undefined') return relay;
  return { ...relay, base: relay.base.replace('//localhost', `//${location.hostname}`) };
}

/** WhatsApp chat with support, with a prefilled message. */
export function supportLink(whatsapp: string, message: string): string | null {
  const digits = whatsapp.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : null;
}

type Gate =
  | { kind: 'loading' }
  | { kind: 'unsupported' }
  | { kind: 'auth'; notice?: string }
  | { kind: 'unverified'; message: string }
  | { kind: 'ready'; session: StoredSession };

export function AccountGate({ children }: { children: ReactNode }) {
  const { catalog, removeSource, task } = useCatalog();
  const [gate, setGate] = useState<Gate>({ kind: 'loading' });
  const device = useRef<DeviceIdentity | null>(null);
  const taskRef = useRef(task);
  taskRef.current = task;

  /** Removes the playlist and its channels from the device. */
  const wipeContent = useCallback(async () => {
    taskRef.current?.cancel();
    for (const source of await catalog.listSources().catch(() => [])) await removeSource(source);
  }, [catalog, removeSource]);

  const endSession = useCallback(
    async (notice?: string) => {
      writeSession(null);
      configureRelay(null);
      setGate({ kind: 'auth', notice });
      await wipeContent();
    },
    [wipeContent],
  );

  const show = useCallback((session: StoredSession) => {
    // Before the playlist loads, so its requests already use the relay.
    configureRelay(reachableRelay(session.data.relay));
    setUhdRelayCut(session.data.uhdRelayCut === true);
    setGate({ kind: 'ready', session });
  }, []);

  const accept = useCallback(
    (token: string, data: SessionData) => {
      const next: StoredSession = { token, data, clockOffset: data.serverTime - Date.now(), verifiedAt: Date.now() };
      writeSession(next);
      show(next);
    },
    [show],
  );

  const verify = useCallback(async () => {
    const current = readSession();
    if (!current) return;
    const result = await api.checkSession(current.token);
    if (result.ok) return accept(current.token, result.data);
    if (result.code === 'offline' || result.code === 'server-error' || result.code === 'not-configured') {
      const { expiresAt, kind } = current.data.account;
      if (current.data.access === 'active' && expiresAt !== null && Date.now() + current.clockOffset >= expiresAt) {
        // Ended while offline: lock now; the server confirms on the next check.
        accept(current.token, { ...current.data, access: kind === 'trial' ? 'trial-ended' : 'expired', playlist: null, relay: null });
      } else if (current.data.access === 'active' && Date.now() - current.verifiedAt > OFFLINE_GRACE_MS[kind]) {
        setGate({ kind: 'unverified', message: result.message });
      }
      return;
    }
    await endSession(result.message);
  }, [accept, endSession]);

  useEffect(() => {
    let active = true;
    void deviceIdentity().then((identity) => {
      if (!active) return;
      device.current = identity;
      if (!identity || !api.accountApiBase()) return setGate({ kind: 'unsupported' });
      const current = readSession();
      if (!current) return setGate({ kind: 'auth' });
      show(current);
      void verify();
    });
    return () => {
      active = false;
    };
  }, [show, verify]);

  const ready = gate.kind === 'ready' ? gate.session : null;
  const access = ready?.data.access ?? null;
  const expiresAt = ready?.data.account.expiresAt ?? null;
  const clockOffset = ready?.clockOffset ?? 0;
  const viaRelay = Boolean(ready?.data.relay);
  useEffect(() => {
    if (!access) return undefined;
    const every = access === 'trial-pending' ? PENDING_CHECK_INTERVAL_MS : viaRelay ? RELAY_CHECK_INTERVAL_MS : CHECK_INTERVAL_MS;
    const interval = setInterval(() => void verify(), every);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void verify();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Check right when the trial or subscription ends (timers cap at ~24.8 days).
    const untilEnd = access !== 'active' || expiresAt === null ? null : Math.min(2 ** 31 - 1, Math.max(0, expiresAt - (Date.now() + clockOffset)) + 1000);
    const timer = untilEnd === null ? null : setTimeout(() => void verify(), untilEnd);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [access, expiresAt, clockOffset, viaRelay, verify]);

  const authenticate = async (attempt: (device: DeviceIdentity) => Promise<ApiResult<SessionData & { token: string }>>): Promise<string | null> => {
    if (!device.current) return t('auth.deviceUnsupported');
    const result = await attempt(device.current);
    if (!result.ok) return result.message;
    const { token, ...data } = result.data;
    accept(token, data);
    return null;
  };

  /** Runs a call with the session token; a session answer updates the screen. */
  const withToken = useCallback(
    async <T,>(call: (token: string) => Promise<ApiResult<T>>, isSession = false): Promise<ApiResult<T>> => {
      const current = readSession();
      if (!current) return { ok: false, code: 'session-ended', message: t('auth.loginAgain') };
      const result = await call(current.token);
      if (result.ok && isSession) accept(current.token, result.data as SessionData);
      return result;
    },
    [accept],
  );

  const logOut = useCallback(() => {
    const current = readSession();
    if (current) void api.logout(current.token);
    void endSession();
  }, [endSession]);

  const removeAccount = useCallback(
    async (password: string) => {
      const result = await withToken((token) => api.deleteAccount(token, password));
      if (!result.ok) return result.message;
      await endSession(t('auth.accountDeleted'));
      return null;
    },
    [withToken, endSession],
  );

  if (gate.kind === 'loading') return <div className="gate" aria-busy="true" />;
  if (gate.kind === 'unsupported') return <UnsupportedScreen />;
  if (gate.kind === 'auth') {
    return (
      <AuthScreen
        notice={gate.notice}
        onLogin={(username, password) => authenticate((identity) => api.login(username, password, identity))}
        onSignup={(input) => authenticate((identity) => api.signup(input, identity))}
      />
    );
  }
  if (gate.kind === 'unverified') {
    return (
      <GateCard title={t('unverified.title')}>
        <p className="muted">{gate.message}</p>
        <button type="button" className="button primary gate__submit" onClick={() => void verify()}>
          {t('common.tryAgain')}
        </button>
        <button type="button" className="button ghost gate__submit" onClick={logOut}>
          {t('common.logOut')}
        </button>
      </GateCard>
    );
  }
  const { data, clockOffset: offset } = gate.session;
  const value: Omit<AccountState, 'reloadPlaylist'> = {
    refreshSession: verify,
    account: data.account,
    access: data.access,
    trialHours: data.trialHours,
    supportWhatsapp: data.support.whatsapp,
    serverNow: () => Date.now() + offset,
    logOut,
    deleteAccount: removeAccount,
    sports: () => withToken((token) => api.getSports(token)),
    matchQuestion: async (matchId, streamId) => {
      const result = await withToken((token) => api.sportsQuestion(token, matchId, streamId));
      return result.ok && result.data.ask;
    },
    answerMatchQuestion: async (matchId, streamId, yes) => {
      const result = await withToken((token) => api.sportsVote(token, matchId, streamId, yes));
      return result.ok && result.data.ok;
    },
    reportSearch: (search) => void withToken((token) => api.reportSearch(token, search)),
    catalog: data.catalog,
    setCatalog: async (choice) => {
      const result = await withToken((token) => api.setCatalogLanguages(token, choice), true);
      return result.ok ? null : result.message;
    },
    playback: {
      pooled: data.pooled,
      start: (watching, replaces, stop) => withToken((token) => api.startPlayback(token, watching, replaces, stop)),
      heartbeat: (leaseId, watching) => withToken((token) => api.playbackHeartbeat(token, leaseId, watching)),
      stop: (leaseId) => void withToken((token) => api.stopPlayback(token, leaseId)),
    },
  };
  if (data.access === 'verify-email') {
    return (
      <VerifyEmailScreen
        state={value}
        onVerify={async (code) => {
          const result = await withToken((token) => api.verifyEmail(token, code), true);
          return result.ok ? null : result.message;
        }}
        onResend={async () => {
          const result = await withToken((token) => api.resendCode(token));
          return result.ok ? null : result.message;
        }}
      />
    );
  }
  // On the line pool, the channel list holds only the viewer's languages: asked once, before it loads.
  if (data.access === 'active' && data.playlist && data.pooled && !data.catalog) {
    return (
      <LanguagesScreen state={value} suggested={data.suggestedLanguages} />
    );
  }
  if (data.access !== 'active' || !data.playlist) {
    return (
      <NoAccessScreen
        state={value}
        onWipe={wipeContent}
        onRequestTrial={async () => {
          if (!device.current) return t('auth.deviceUnsupported');
          const identity = device.current;
          const result = await withToken((token) => api.requestTrial(token, identity), true);
          return result.ok ? null : result.message;
        }}
      />
    );
  }
  return (
    <ActiveAccount state={value} playlist={data.playlist} catalog={data.catalog}>
      {children}
    </ActiveAccount>
  );
}

function ActiveAccount({ state, playlist, catalog, children }: { state: Omit<AccountState, 'reloadPlaylist'>; playlist: Playlist; catalog: CatalogChoice | null; children: ReactNode }) {
  const [attempt, setAttempt] = useState(0);
  const { refreshSession } = state;
  const reloadPlaylist = useCallback(() => {
    // A fresh session first: the relay may have moved since the last check.
    void refreshSession().then(() => setAttempt((n) => n + 1));
  }, [refreshSession]);
  usePlaylistSync(playlist, attempt, reloadPlaylist, catalog);
  return <AccountContext.Provider value={{ ...state, reloadPlaylist }}>{children}</AccountContext.Provider>;
}

const CATALOG_CHOICE_KEY = 'viva.catalogChoice';

/** The language choice the stored channel list was loaded with (storage can be unavailable). */
function loadedChoice(): string | null {
  try {
    return localStorage.getItem(CATALOG_CHOICE_KEY);
  } catch {
    return null;
  }
}

function rememberChoice(choice: string): void {
  try {
    localStorage.setItem(CATALOG_CHOICE_KEY, choice);
  } catch {
    // Without storage the list is loaded again next time, which only costs time.
  }
}

/** Keeps exactly the account's playlist in the catalog, loaded with the viewer's languages. */
function usePlaylistSync(playlist: Playlist, attempt: number, retry: () => void, catalog: CatalogChoice | null): void {
  const { sources, task, addSource, refreshSource, removeSource } = useCatalog();
  const choice = catalog ? JSON.stringify({ languages: [...catalog.languages].sort(), international: catalog.international }) : '';
  const reloadedFor = useRef<string | null>(null);
  const tried = useRef<string | null>(null);
  const retried = useRef(false);
  const resumed = useRef(false);
  const wanted = `${JSON.stringify(playlist)}#${attempt}`;
  useEffect(() => {
    if (sources === null || task) return;
    const matching = sources.filter((source) => sameConfig(source.config, playlist.config));
    for (const source of sources) if (!matching.includes(source)) void removeSource(source);
    // New languages chosen (or a list loaded before languages existed): load it again with them.
    const current = matching[0];
    if (current && choice && loadedChoice() !== choice && reloadedFor.current !== choice) {
      reloadedFor.current = choice;
      void refreshSource(current).then((ok) => ok && rememberChoice(choice));
      return;
    }
    // A first load that stopped part way (app closed, connection lost): finish it once per launch.
    const partial = matching.find((source) => source.lastSync?.pending);
    if (partial && !resumed.current) {
      resumed.current = true;
      void refreshSource(partial);
      return;
    }
    // Automatic attempts: one, plus one retry with a fresh session (the relay may
    // have moved). After that the error stays and "Load channels" retries.
    if (matching.length === 0 && tried.current !== wanted) {
      tried.current = wanted;
      void addSource({ name: playlist.name, config: playlist.config }).then((ok) => {
        if (ok && choice) rememberChoice(choice);
        if (ok || retried.current) return;
        retried.current = true;
        retry();
      });
    }
  }, [sources, task, playlist, wanted, addSource, refreshSource, removeSource, retry, choice]);
}

/** The app's language, on every login and account screen (and in Account). */
export function LanguageSelect() {
  const current = useLanguage();
  return (
    <label className="language-select">
      <span className="visually-hidden">{t('language.label')}</span>
      <select value={current} onChange={(event) => setLanguage(event.target.value as Language)} aria-label={t('language.label')}>
        {LANGUAGES.map((entry) => (
          <option key={entry.code} value={entry.code}>
            {entry.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function GateCard({ title, children }: { title: string; children: ReactNode }) {
  useLanguage();
  return (
    <div className="gate">
      <GateBackdrop />
      <div className="gate__card panel">
        <div className="gate__brand">
          <img className="logo-mark" src={`${import.meta.env.BASE_URL}logo-mark.png`} alt="" aria-hidden="true" />
          <span>
            IPT<span className="nav__brand-accent">Viva</span>
          </span>
          <span className="gate__spacer" />
          <LanguageSelect />
        </div>
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

/** Form submit with a busy state and an error message. */
function useSubmit(action: () => Promise<string | null>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    const failure = await action();
    setBusy(false);
    setError(failure);
  };
  return { busy, error, setError, submit };
}

function Notice({ children, role }: { children: ReactNode; role?: 'alert' | 'status' }) {
  return (
    <div className={`gate__notice ${role === 'status' ? 'gate__notice--ok' : ''}`} role={role}>
      {children}
    </div>
  );
}

type AuthMode = 'login' | 'signup' | 'forgot';

function AuthScreen({
  notice,
  onLogin,
  onSignup,
}: {
  notice?: string;
  onLogin: (username: string, password: string) => Promise<string | null>;
  onSignup: (input: SignupInput) => Promise<string | null>;
}) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [email, setEmail] = useState('');
  useLanguage();
  // The country of the website store they came from (iptviva.com/fr → France), else the United Kingdom (owner's choice).
  const [country, setCountry] = useState<Country>(() => COUNTRIES.find((candidate) => candidate.iso === (market() ?? 'GB')) ?? guessCountry());
  const pickCountry = setCountry;
  const [remember, setRemember] = useState(rememberLogin);
  const [whatsapp, setWhatsapp] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const form = useSubmit(async () => {
    setRememberLogin(remember);
    if (mode !== 'signup') return onLogin(email.trim(), password);
    if (!name.trim()) return t('auth.enterName');
    if (!isStrongPassword(password)) return t('auth.weakPassword');
    return onSignup({ email: email.trim(), whatsapp: fullNumber(country, whatsapp), password, name: name.trim() });
  });

  if (mode === 'forgot') {
    return (
      <ForgotPassword
        initialEmail={email}
        onDone={(changed) => {
          setMode('login');
          setPasswordChanged(changed);
        }}
      />
    );
  }
  const signingUp = mode === 'signup';
  return (
    <GateCard title={signingUp ? t('auth.signupTitle') : t('auth.login')}>
      <div className="segmented gate__tabs" role="tablist">
        {(['login', 'signup'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={mode === option}
            className="segmented__item"
            onClick={() => {
              setMode(option);
              form.setError(null);
            }}
          >
            {option === 'login' ? t('auth.login') : t('auth.signup')}
          </button>
        ))}
      </div>
      <p className="muted">{signingUp ? t('auth.signupLead') : t('auth.loginLead')}</p>
      {notice && !form.error && !passwordChanged ? <Notice>{notice}</Notice> : null}
      {passwordChanged && !form.error ? <Notice role="status">{t('auth.passwordChanged')}</Notice> : null}
      <form onSubmit={form.submit}>
        {signingUp ? (
          <label className="field">
            <span>{t('auth.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={120} required />
          </label>
        ) : null}
        <label className="field">
          <span>{signingUp ? t('auth.email') : t('auth.emailOrUsername')}</span>
          <input
            type={signingUp ? 'email' : 'text'}
            id="username"
            name="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode={signingUp ? 'email' : undefined}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete={signingUp ? 'email' : 'username'}
            spellCheck={false}
            required
          />
        </label>
        {signingUp ? (
          <div className="field">
            <span>{t('auth.whatsapp')}</span>
            <PhoneField country={country} onCountryChange={pickCountry} number={whatsapp} onNumberChange={setWhatsapp} />
            <small className="muted field__hint">{t('auth.whatsappHint')}</small>
          </div>
        ) : null}
        <label className="field">
          <span>{t('auth.password')}</span>
          <input
            type="password"
            id="password"
            name="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              // A "too weak" warning from an earlier try goes away while the password is fixed.
              if (signingUp) form.setError(null);
            }}
            autoComplete={signingUp ? 'new-password' : 'current-password'}
            required
          />
        </label>
        {signingUp ? <PasswordChecklist password={password} /> : null}
        <label className="checkbox">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>{t('auth.keepLoggedIn')}</span>
        </label>
        {form.error ? <Notice role="alert">{form.error}</Notice> : null}
        <button type="submit" className="button primary gate__submit" disabled={form.busy}>
          {form.busy ? t('auth.wait') : signingUp ? t('auth.signup') : t('auth.login')}
        </button>
      </form>
      {!signingUp ? (
        <button type="button" className="gate__link" onClick={() => setMode('forgot')}>
          {t('auth.forgot')}
        </button>
      ) : null}
    </GateCard>
  );
}

/** The password rules, ticked off live as the visitor types. */
function PasswordChecklist({ password }: { password: string }) {
  return (
    <ul className="password-rules" aria-label={t('password.rules')}>
      {PASSWORD_RULES.map((rule) => {
        const ok = rule.test(password);
        return (
          <li key={rule.label} className={ok ? 'password-rules__ok' : undefined}>
            <span className="password-rules__mark" aria-hidden="true">
              {ok ? '✓' : '•'}
            </span>
            {t(rule.label)}
            <span className="visually-hidden"> {ok ? t('password.done') : t('password.missing')}</span>
          </li>
        );
      })}
    </ul>
  );
}

function ForgotPassword({ initialEmail, onDone }: { initialEmail: string; onDone: (changed: boolean) => void }) {
  const [email, setEmail] = useState(initialEmail.includes('@') ? initialEmail : '');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const form = useSubmit(async () => {
    if (!sent) {
      const result = await api.requestPasswordReset(email.trim());
      if (!result.ok) return result.message;
      setSent(true);
      return null;
    }
    if (!isStrongPassword(password)) return t('auth.weakPassword');
    const result = await api.confirmPasswordReset(email.trim(), code.trim(), password);
    if (!result.ok) return result.message;
    onDone(true);
    return null;
  });
  return (
    <GateCard title={t('reset.title')}>
      <p className="muted">{sent ? t('reset.sent', { email }) : t('reset.lead')}</p>
      <form onSubmit={form.submit}>
        {!sent ? (
          <label className="field">
            <span>{t('auth.email')}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoCapitalize="none" required />
          </label>
        ) : (
          <>
            <label className="field">
              <span>{t('reset.code')}</span>
              <input className="code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={8} required />
            </label>
            <label className="field">
              <span>{t('reset.newPassword')}</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
            </label>
            <PasswordChecklist password={password} />
          </>
        )}
        {form.error ? <Notice role="alert">{form.error}</Notice> : null}
        <button type="submit" className="button primary gate__submit" disabled={form.busy}>
          {form.busy ? t('auth.wait') : sent ? t('reset.save') : t('reset.send')}
        </button>
      </form>
      <button type="button" className="gate__link" onClick={() => onDone(false)}>
        {t('reset.back')}
      </button>
    </GateCard>
  );
}

/** Asked once on the line pool, before the channel list loads: it will hold these languages only. */
function LanguagesScreen({ state, suggested }: { state: Omit<AccountState, 'reloadPlaylist'>; suggested: string[] }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <GateCard title={t('catalog.title')}>
      <p className="muted">{t('catalog.lead')}</p>
      <LanguagePicker
        initial={{ languages: suggested, international: false }}
        busy={busy}
        submitLabel={t('catalog.continue')}
        onSubmit={(choice) => {
          setBusy(true);
          void state.setCatalog(choice).then((failure) => {
            setBusy(false);
            setError(failure);
          });
        }}
      />
      {error ? <Notice role="alert">{error}</Notice> : null}
      <button type="button" className="gate__link" onClick={state.logOut}>
        {t('common.logOut')}
      </button>
    </GateCard>
  );
}

function VerifyEmailScreen({
  state,
  onVerify,
  onResend,
}: {
  state: Omit<AccountState, 'reloadPlaylist'>;
  onVerify: (code: string) => Promise<string | null>;
  onResend: () => Promise<string | null>;
}) {
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);
  const form = useSubmit(() => onVerify(code.trim()));
  const resend = useSubmit(async () => {
    const failure = await onResend();
    setResent(failure === null);
    return failure;
  });
  return (
    <GateCard title={t('verify.title')}>
      <p className="muted">{t('verify.lead', { email: state.account.email ?? '' })}</p>
      <form onSubmit={form.submit}>
        <label className="field">
          <span>{t('verify.code')}</span>
          <input className="code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={8} required />
        </label>
        {form.error ?? resend.error ? <Notice role="alert">{form.error ?? resend.error}</Notice> : null}
        {resent && !resend.error ? <Notice role="status">{t('verify.newCodeSent')}</Notice> : null}
        <button type="submit" className="button primary gate__submit" disabled={form.busy}>
          {form.busy ? t('verify.checking') : t('verify.confirm')}
        </button>
      </form>
      <button type="button" className="button ghost gate__submit" disabled={resend.busy} onClick={() => void resend.submit()}>
        {t('verify.resend')}
      </button>
      <button type="button" className="gate__link" onClick={state.logOut}>
        {t('common.logOut')}
      </button>
    </GateCard>
  );
}

/** Text keys of each screen without access (the site no longer gives a trial length, so neither does the app). */
const NO_ACCESS: Record<Exclude<Access, 'active' | 'verify-email'>, 'noPlan' | 'pending' | 'used' | 'trialEnded' | 'expired'> = {
  'no-plan': 'noPlan',
  'trial-pending': 'pending',
  'trial-used': 'used',
  'trial-ended': 'trialEnded',
  expired: 'expired',
};

function NoAccessScreen({
  state,
  onRequestTrial,
  onWipe,
}: {
  state: Omit<AccountState, 'reloadPlaylist'>;
  onRequestTrial: () => Promise<string | null>;
  onWipe: () => Promise<void>;
}) {
  useLanguage();
  const access = state.access as keyof typeof NO_ACCESS;
  const copy = NO_ACCESS[access];
  const request = useSubmit(onRequestTrial);
  const [deleting, setDeleting] = useState(false);
  // Channels from an ended plan must not stay on the device.
  useEffect(() => {
    void onWipe();
  }, [onWipe]);
  const contact = supportLink(state.supportWhatsapp, `${t(`noAccess.${copy}.contact`)} (${state.account.email ?? state.account.username})`);
  const waiting = access === 'no-plan' || access === 'trial-pending';

  return (
    <GateCard title={t(`noAccess.${copy}.title`)}>
      <p className="muted">{t(`noAccess.${copy}.body`)}</p>
      {request.error ? <Notice role="alert">{request.error}</Notice> : null}
      {access === 'no-plan' ? (
        <button type="button" className="button primary gate__submit" disabled={request.busy} onClick={() => void request.submit()}>
          {request.busy ? t('noAccess.sending') : t('noAccess.request')}
        </button>
      ) : null}
      {access === 'trial-pending' ? (
        <div className="gate__waiting" role="status">
          <span className="gate__spinner" aria-hidden="true" /> {t('noAccess.waiting')}
        </div>
      ) : null}
      {contact ? (
        <a className={`button gate__submit ${waiting ? '' : 'primary'}`} href={contact} target="_blank" rel="noreferrer">
          {waiting ? t('noAccess.contactWhatsapp') : t('noAccess.subscribeWhatsapp')}
        </a>
      ) : null}
      <button type="button" className="button ghost gate__submit" onClick={state.logOut}>
        {t('common.logOut')}
      </button>
      <p className="muted small gate__device">{t('noAccess.loggedInAs', { name: state.account.email ?? state.account.username })}</p>
      {deleting ? (
        <DeleteAccountForm onDelete={state.deleteAccount} onCancel={() => setDeleting(false)} />
      ) : (
        <button type="button" className="gate__link" onClick={() => setDeleting(true)}>
          {t('delete.link')}
        </button>
      )}
    </GateCard>
  );
}

/** Account deletion, confirmed with the password (required by the App Store). */
export function DeleteAccountForm({ onDelete, onCancel }: { onDelete: (password: string) => Promise<string | null>; onCancel: () => void }) {
  const [password, setPassword] = useState('');
  const form = useSubmit(() => onDelete(password));
  useLanguage();
  return (
    <form className="delete-account" onSubmit={form.submit}>
      <strong>{t('delete.title')}</strong>
      <p className="muted small">{t('delete.body')}</p>
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.password')} autoComplete="current-password" required />
      {form.error ? <Notice>{form.error}</Notice> : null}
      <div className="delete-account__actions">
        <button type="button" className="button ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="button danger" disabled={form.busy}>
          {form.busy ? t('delete.deleting') : t('delete.confirm')}
        </button>
      </div>
    </form>
  );
}

function UnsupportedScreen() {
  return (
    <GateCard title={t('unavailable.title')}>
      <p className="muted">{api.accountApiBase() ? t('unavailable.device') : t('unavailable.build')}</p>
    </GateCard>
  );
}
